import { memo, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { ArrayUtils, CommonUtils } from 'src/utils';
import { useAsyncFn } from 'src/hooks/useAsyncFn';
import { CommonService } from 'src/services';
import { provinceSelector } from 'src/selectors/app';
import { Element, Image, LazyLoad, RouterLink } from 'src/components';

import { GoldBox } from './gold';
import { ExchangeBox } from './exchange';
import { StockBox } from './stock';

export function UtilitiesSection() {
    const province = useSelector(provinceSelector);
    const [boardId, setBoardId] = useState<string>('hanoi');
    const scrollRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (province) {
            setBoardId(CommonUtils.getBoardIdFromProvince(province));
        }
    }, [province]);

    return (
        <Element className={cn('leading-[1.3] my-[24px]')}>
            <Element
                domRef={scrollRef}
                className={cn(
                    'flex overflow-x-auto gap-[8px]',
                    '[scrollbar-width:none]',
                    '[&::-webkit-scrollbar]:hidden',
                )}
            >
                <WeatherBox boardId={boardId}></WeatherBox>
                <GoldBox />
                <ExchangeBox />
                <StockBox />
            </Element>
        </Element>
    );
}

function WeatherBox({ boardId }: { boardId: string }) {
    const [state, callback] = useAsyncFn<any>(CommonService.getWeatherDetail);

    const data = state.value?.data || {};

    useEffect(() => {
        if (boardId) {
            callback(boardId);
        }
    }, [boardId]);

    const { entries = [], displayName, temperatureMax, temperatureMin } = data;

    if (ArrayUtils.isEmpty(entries)) {
        return null;
    }

    const weather = entries[0];
    const currentEntry = data.currentEntry;

    const getBgImg = () => {
        switch (weather.iconCode) {
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
            case 8:
            case 9:
            case 10:
            case 11:
            case 12:
            case 17:
            case 18:
            case 35:
            case 38:
            case 39:
            case 40:
            case 47:
                return 'weather-rainy';
            case 13:
            case 14:
            case 15:
            case 16:
            case 19:
            case 20:
            case 21:
            case 22:
            case 23:
            case 24:
            case 25:
            case 26:
            case 27:
            case 42:
            case 43:
            case 44:
                return 'weather-cloudy';

            default:
                return 'weather-sunny';
        }
    };

    return (
        <RouterLink
            to="/tien-ich-thoi-tiet.epi"
            className={cn(
                'p-[6px_8px_18px_16px]',
                'rounded-[4px]',
                'w-[210px]',
                'bg-cover',
                'bg-center',
                'relative',
                'flex justify-between items-end',
                'shrink-0',
            )}
            style={{
                backgroundImage: `url(https://baomoi-static.bmcdn.me/web/styles/img/${getBgImg()}.png)`,
            }}
        >
            <Element className="relative">
                <Element
                    className={cn('flex items-center', 'leading-[normal]')}
                >
                    <Element className="text-[10px] font-bold leading-[1.6]">
                        {displayName}
                    </Element>
                </Element>
                <Element
                    className={'text-[18px] leading-[1.2] font-[800] shrink-0'}
                >
                    {currentEntry?.status}
                </Element>
            </Element>
            <Element className={cn('relative flex flex-col items-center')}>
                <Image src={weather.iconUrl} className="w-[32px] h-[32px]" />
                <Element
                    renderAs="span"
                    className="text-[14px] font-bold leading-[1] mt-[4px]"
                >
                    {temperatureMin}&ordm; - {temperatureMax}&ordm;
                </Element>
            </Element>
        </RouterLink>
    );
}

export default memo(UtilitiesSection);
